import { useState, useRef, useCallback } from 'react';

type RecorderState = 'idle' | 'recording';

export interface UseVoiceRecorderReturn {
  state: RecorderState;
  error: string | null;
  start: () => Promise<void>;
  stop: () => Promise<{ blob: Blob; mimeType: string }>;
  _recorder?: MediaRecorder | null;
}

function stopStreamTracks(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => t.stop());
}

export function useVoiceRecorder(): UseVoiceRecorderReturn {
  const [state, setState] = useState<RecorderState>('idle');
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      streamRef.current = stream;
      chunksRef.current = [];

      recorder.ondataavailable = (event: { data: Blob }) => {
        chunksRef.current.push(event.data);
      };

      recorder.start();
      setState('recording');
    } catch {
      setError('microphoneDenied');
    }
  }, []);

  const stop = useCallback((): Promise<{ blob: Blob; mimeType: string }> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder) {
        resolve({ blob: new Blob([]), mimeType: 'audio/webm' });
        return;
      }

      const mimeType = recorder.mimeType;

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        stopStreamTracks(streamRef.current);
        streamRef.current = null;
        setState('idle');
        resolve({ blob, mimeType });
      };

      recorder.stop();
    });
  }, []);

  return {
    state,
    error,
    start,
    stop,
    _recorder: recorderRef.current,
  };
}
